<script setup>

import {ErrorMessage, Field, Form} from 'vee-validate'
import {computed, reactive, ref} from 'vue'
import axios from 'axios'
import {useAddressField} from '../../composables/useAddressField'
import GoogleAddress from './../Google/GoogleAddressModal.vue'
import useGlobalStore from '../../store/globalStore'
import cookieHelper from '../cookie'
import {useFormSubmit} from '../../composables/useFormSubmit'

const {showAddressFields, addressValueChange, showCustomAddressLink, toggleCustomAddress} = useAddressField()
const { hideModal, showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()

const validationSchema = computed(() => {
  let schema = {
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    phone_number: 'required|phoneNumber',
  }

  if (showAddressFields.value) {
    schema.street_address = 'required'
    schema.postcode = 'required|numeric'
    schema.city = 'required'
  }

  return schema
})

const confirmation = reactive({show: false, message: '', class: ''})
let isSubmitting = ref(false)
const globalStore = useGlobalStore()

const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('village-enquiry-form');
  let formData = new FormData(form);
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function (response) {
      confirmation.class = 'message--success'
      confirmation.message = response.data.msg
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      let currentUrl = window.location.href;
      currentUrl = currentUrl.replace('/info-pack', '');
      currentUrl = currentUrl.replace('#enquiry-submitted', '');
      history.pushState(null, null, `${currentUrl}#enquiry-submitted`);
      const modalTitle = globalStore.modal.title;

      if (cookieHelper.getCookie('villageInfoSuccess') === null) {
        globalStore.setModal(
          {
            name: 'villageInfoPackSuccessModal',
            title: modalTitle,
          }
        )
        cookieHelper.setCookie('villageInfoSuccess', 'true', 365);
      } else {
        hideModal()
      }
    }).catch(error => {
      submitButton.disabled = false;
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    v-slot="{ errors }"
    @submit="onSubmit"
    class="modal-form modal-form--2column"
    id="village-enquiry-form"
  >
    <slot name="token"></slot>
    <div class="row">
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="name" class="sr-only">First name</label>
          <Field
            id="first_name"
            name="first_name"
            class="form-control"
            :class="{'is-danger': errors['first_name'] }"
            placeholder="First name *"
            type="text"
          />
          <ErrorMessage name="first_name" class="is-danger" />
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="last_name" class="sr-only">Last name</label>
          <Field
            name="last_name"
            class="form-control"
            :class="{'is-danger': errors['last_name'] }"
            id="last_name"
            placeholder="Last name *"
            type="text"
          />
          <ErrorMessage name="last_name" class="is-danger" />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="email" class="sr-only">Email</label>
          <Field
            id="email"
            name="email"
            class="form-control"
            :class="{'is-danger': errors['email'] }"
            type="email"
            placeholder="Email *"
          />
          <ErrorMessage name="email" class="is-danger" />
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="phone_number" class="sr-only">Phone</label>
          <Field
            id="phone_number"
            name="phone_number"
            class="form-control"
            :class="{'is-danger': errors['phone_number'] }"
            type="text"
            placeholder="Phone *"
          />
          <ErrorMessage name="phone_number" class="is-danger" />
        </div>
      </div>
    </div>
    <div class="row" v-if="showAddressFields">
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input" >
          <label for="street_address" class="sr-only">Street address</label>
          <Field
            id="street_address"
            name="street_address"
            placeholder="Street Address *"
            class="form-control"
            :class="{'is-danger': errors['street_address'] }"
            type="text"
          />
          <ErrorMessage name="street_address" class="is-danger" />
        </div>
        <div class="form-group form-group--input">
          <label for="postcode" class="sr-only">Post code</label>
          <Field
            id="postcode"
            name="postcode"
            class="form-control"
            :class="{'is-danger': errors['postcode'] }"
            placeholder="Post code *"
            type="text"
          />
          <ErrorMessage name="postcode" class="is-danger" />

        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="city" class="sr-only">City</label>
          <Field
            id="city"
            name="city"
            class="form-control"
            :class="{'is-danger': errors['city'] }"
            placeholder="City *"
            type="text"
          />
          <ErrorMessage name="city" class="is-danger" />
        </div>
        <div class="form-group form-group--input">
          <slot name="country"></slot>
        </div>
      </div>
    </div>
    <div class="form-group form-group--input" v-if="!showAddressFields">
      <label for="address" class="sr-only">Address</label>
      <google-address
        :address-id="'village-page-enquiry'"
        v-on:addressvaluechange="addressValueChange"
        :classname="'form-control'"
      ></google-address>
      <div v-if="showCustomAddressLink" class="mt-2 fs-14 font-weight-600" >
        Can't find your address? <a href="#" @click.prevent="toggleCustomAddress" id="enterCustomAddress">Enter it here</a>
      </div>
    </div>

    <div class="form-group form-group--input">
      <label for="message" class="sr-only">Message</label>
      <textarea
        tabindex="9"
        id="message"
        rows="4"
        :placeholder="globalStore.modal.descriptionPlaceholder"
        class="form-control form-control--textarea"
        name="message"
      ></textarea>
    </div>
    <div class="row">
      <div class="col-lg-6">
        <input type="hidden" name="captcha_action" value="village_enquiry">
      </div>
      <div class="col-lg-6">
        <slot name="jobenquiry"></slot>
      </div>
    </div>
    <div tabindex="11" class="checkbox-custom mt-4 mb-4">
      <slot name="subscribe"></slot>
    </div>

    <transition name="fade">
      <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
    </transition>
    <div class="form-group mt-5">
      <slot name="hiddenfields"></slot>
      <button tabindex="12" type="submit" class="btn btn--green btn--send btn--info-pack submit">
        Send
      </button>
    </div>
    <slot name="googlerecaptchatext"></slot>
  </Form>
</template>
